// LIBRERIE/IMPORTS
import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

// CONFIGURAZIONI
import { getContractTranslation, LanguageCode } from "../../config/i18n/elementsMappingProperty";
import { getTipologiaTranslation } from "../../config/i18n/propertyTranslations";

// HOOKS
import { useGetContratti } from "../../hooks/standard/useGetContracts";
// import { useGetTipologie } from "../../hooks/standard/useGetTipologies";
import { useSendEmailSell } from "../../hooks/useSendEmail";

import formStyle from "../../assets/jsStyle/formStyle";

interface Option {
    value: number | string | null;
    label: string | null;
}

const Sell: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Trucco salva pagina del dettaglio immobili su localStorage per mantenere la paginazione dopo refresh o ritorno da dettaglio
    useEffect(() => {
        localStorage.setItem(
            "propertiesPage",
            String(1)
        );
    }, []);

    const [privacyConsent, setPrivacyConsent] = useState(false);

    const { contratti } = useGetContratti();
    // const { tipologie } = useGetTipologie();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const { sendEmail, isLoading, emailError, success } = useSendEmailSell();

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!privacyConsent) {
            alert(t('validation.privacyAcceptanceRequired') || "Devi accettare la privacy policy per inviare il modulo.");
            return;
        }

        // 1. Ottieni FormData originale per leggerne i valori
        const formData = new FormData(e.currentTarget);
        const rawData = Object.fromEntries(formData.entries());

        const privacyConsentString = privacyConsent.toString();

        if (!formData.has('sell_privacy') || formData.get('sell_privacy') === '') {
            formData.append('sell_privacy', privacyConsentString);
        } else {
            formData.set('sell_privacy', privacyConsentString);
        }

        const convertedData: { [key: string]: any } = Object.fromEntries(
            Object.entries(rawData).map(([key, value]) => {
                try {
                    const stringValue = String(value);

                    if (key === 'sell_contratto') {
                        const contractId = Number(stringValue);
                        const translatedLabel = isNaN(contractId)
                            ? null
                            : getContractTranslation(contractId, i18n.language as LanguageCode);

                        return [key, translatedLabel || stringValue || '-'];
                    }

                    if (key === 'sell_tipologia') {
                        const tipologiaId = Number(stringValue);
                        const translatedLabel = isNaN(tipologiaId)
                            ? null
                            : getTipologiaTranslation(tipologiaId, i18n.language as LanguageCode);

                        return [key, translatedLabel || stringValue || '-'];
                    }

                    if (key === 'sell_provincia') {
                        return [key, stringValue || '-'];
                    }

                    if (key === 'sell_comune') {
                        return [key, stringValue || '-'];
                    }

                    if (key === 'sell_frazione') {
                        return [key, stringValue || '-'];
                    }

                    if (stringValue === '') {
                        return [key, '-'];
                    }

                    return [key, stringValue];

                } catch (conversionError) {
                    console.error(`Errore durante la conversione del campo ${key}:`, conversionError);
                    return [key, value];
                }
            })
        );
        console.log("handleSubmit (Sell): Dati convertiti (con label):", convertedData);

        // 2. Esegui ReCAPTCHA
        if (!executeRecaptcha) {
            console.error("handleSubmit (Sell): executeRecaptcha non disponibile");
            alert(t('validation.internalErrorRecaptcha') || "Errore interno: impossibile verificare il modulo.");
            return;
        }

        let recaptchaToken: string | null = null;

        try {
            recaptchaToken = await executeRecaptcha('submitSellForm');
            if (!recaptchaToken) {
                console.warn("handleSubmit (Sell): Token reCAPTCHA non ottenuto");
                alert(t('validation.recaptchaTokenError') || "Errore: impossibile verificare che sei umano. Riprova.");
                return;
            }
            console.log("handleSubmit (Sell): Token ReCaptcha ottenuto.");
        } catch (error) {
            console.error("handleSubmit (Sell): Errore durante l'esecuzione di reCAPTCHA:", error);
            alert(t('validation.recaptchaExecutionError') || "Si è verificato un errore durante la verifica reCAPTCHA.");
            return;
        }

        // 3. Chiama funzione sendEmail()
        sendEmail({
            data: convertedData,
            recaptchaToken: recaptchaToken
        });

        // 4. Logica Iubenda (dopo aver INIZIATO la chiamata a sendEmail)
        console.log("Tentativo invio consenso Iubenda...");
        // Inizializza la Consent Solution di Iubenda
        if (window._iub && window._iub.cons_instructions) {
            console.log("window._iub trovato. Invio comando submit.");
            const formElement = document.getElementById("sell_form");
            const emailFieldName = "sell_email";
            const nameFieldName = "sell_sender";
            const privacyCheckboxName = "generic";
            console.log("Form Selettore:", formElement);
            console.log("Mapping Nomi:", nameFieldName, emailFieldName, privacyCheckboxName);
            if (formElement) {
                window._iub.cons_instructions.push(["submit", {
                    form: {
                        selector: formElement,
                        map: {
                            subject: { full_name: nameFieldName, email: emailFieldName },
                            preferences: { privacy_policy: "generic" }
                        },
                        consent: {
                            legal_notices: [{ identifier: privacyCheckboxName }]
                        }
                    }
                }]);
                console.log("Comando submit inviato a Iubenda.");
            } else {
                console.error("Elemento form #sell_form non trovato!");
            }
        } else {
            console.error("ERRORE: window._iub o window._iub.cons_instructions non disponibili!");
        };
    }, [t, privacyConsent, executeRecaptcha, sendEmail, i18n.language]);

    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrivacyConsent(e.target.checked);
    };

    // Helper per prevenire lo scroll su focus dei Select
    const preventScrollOnFocus = (e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <>
            <Helmet>
                <title>Valuta la tua casa</title>
            </Helmet>

            <main className="space-ptb valuta-page">
                <div className="container-fluid w-90">
                    <div className="row justify-content-around">
                        <div className="col-lg-5 mb-4 mb-lg-0">
                            <h3 className="text-center" dangerouslySetInnerHTML={{ __html: t('sellRequestForm.formTitle') as string }}></h3>
                        </div>
                        <div className="col-lg-7 mt-4 mt-lg-0">

                            <div id="lead-sell-message" style={{ marginBottom: '1rem' }}>
                                {success && (
                                    <div className="alert alert-success" role="alert">
                                        {t('statusMessages.messageSentSuccessfully') || "Richiesta inviata con successo!"}
                                    </div>
                                )}
                                {emailError && (
                                    <div className="alert alert-danger" role="alert">
                                        {typeof emailError === 'string' ? emailError : t('validation.genericRequestError', 'Si è verificato un errore.')}
                                    </div>
                                )}
                            </div>

                            <form
                                id="sell_form"
                                onSubmit={handleSubmit}
                                name="lead_sell_form"
                                acceptCharset="utf-8"
                            >
                                <div className="form-row">

                                    <h6 className="col-12 mb-3">{t('sellRequestForm.personalInfoSectionTitle')}</h6>

                                    <div className="col-md-12 mb-3">
                                        <div className="form-group">
                                            <input
                                                type="text"
                                                id="sell_sender"
                                                name="sell_sender"
                                                className="form-control"
                                                placeholder={t('sellRequestForm.nameSurnamePlaceholder') || "Nome e Cognome"}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input
                                                type="email"
                                                id="sell_email"
                                                name="sell_email"
                                                className="form-control"
                                                placeholder={t('sellRequestForm.emailPlaceholder') || "Email"}
                                                required
                                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                                title={t('validation.emailPatternTitle') || "Inserisci un indirizzo email valido, es. esempio@dominio.com"}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input
                                                type="text"
                                                id="sell_tel"
                                                name="sell_tel"
                                                className="form-control"
                                                placeholder={t('sellRequestForm.phonePlaceholder') || "Telefono"}
                                                required
                                                pattern="^\+?(\d[\d-. ]+)?(\([\d-. ]+\))?[\d-. ]+\d$"
                                                title={t('validation.phonePatternTitle') || "Inserisci un numero di telefono corretto, es. +39 345 678 9999"}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <div className="form-group">
                                            <textarea
                                                id="input_message"
                                                name="sell_message"
                                                cols={50} rows={5}
                                                className="form-control"
                                                placeholder={t('sellRequestForm.messagePlaceholder') || "Messaggio..."}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-12 mt-3 mb-4">
                                        <hr />
                                    </div>

                                    <h6 className="col-12 mb-3">{t('sellRequestForm.propertyInfoSectionTitle')}</h6>

                                    {/* Contratto */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <Select<Option, false>
                                                className=""
                                                id="input_contratto"
                                                placeholder={t('sellRequestForm.contractPlaceholder') || "Contratto"}
                                                required
                                                name="sell_contratto"
                                                options={Array.isArray(contratti) ? contratti.filter((c) => c.id !== 5).map((c) => ({ value: c.id, label: getContractTranslation(c.id, i18n.language as LanguageCode) })) : []}
                                                menuPortalTarget={document.body}
                                                styles={formStyle}
                                                onFocus={preventScrollOnFocus}
                                            />
                                        </div>
                                    </div>

                                    {/* Tipologia */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input type="text" name="sell_tipologia" id="input_tipologia" className="form-control" placeholder={t('sellRequestForm.typePlaceholder') || "Tipologia"} required />
                                        </div>
                                    </div>

                                    {/* Provincia */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input type="text" name="sell_provincia" id="input_provincia" className="form-control" placeholder={t('sellRequestForm.provincePlaceholder') || "Provincia"} required />
                                        </div>
                                    </div>

                                    {/* Comune */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input type="text" name="sell_comune" id="input_comune" className="form-control" placeholder={t('sellRequestForm.cityPlaceholder') || "Comune"} required />
                                        </div>
                                    </div>

                                    {/* Frazione */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input type="text" name="sell_frazione" id="input_frazione" className="form-control" placeholder={t('sellRequestForm.fractionPlaceholder') || "Frazione"} required />
                                        </div>
                                    </div>

                                    {/* Superficie */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <input
                                                type="number"
                                                id="sell_mq"
                                                name="sell_mq"
                                                className="form-control"
                                                placeholder={t('sellRequestForm.surfacePlaceholder') || "Superficie (mq)"}
                                            min="0"
                                            step="10"
                                        />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <div className="custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                id="sell_privacy"
                                                name="generic"
                                                value="1"
                                                className="custom-control-input"
                                                required
                                                checked={privacyConsent}
                                                onChange={handlePrivacyChange}
                                            />
                                            <label className="custom-control-label" htmlFor="sell_privacy">
                                                {t('sellRequestForm.privacyConsentText') || "Accetto l'"}
                                                <Link to="/privacy" title={t('privacyPolicy.title') || "Privacy Policy"}>
                                                    {t('sellRequestForm.privacyPolicyLinkText') || "informativa sulla Privacy"}
                                                </Link>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <button
                                            id="contact-submit"
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={isLoading}
                                        >
                                            <span>
                                                {isLoading ? (t('sellRequestForm.sendingButton') || 'Invio...') : (t('sellRequestForm.submitButton') || 'Invia Richiesta')}
                                            </span>
                                            <span>
                                                {isLoading ? (t('sellRequestForm.sendingButton') || 'Invio...') : (t('sellRequestForm.submitButton') || 'Invia Richiesta')}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                </div>
            </main>
        </>
    );
};

export default Sell;